import { createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'src/store';
import { fetchWithRetry } from 'src/utils/fetch-retry';
import { clientSession, isQuizResults } from 'src/utils/session-storage';

let resultsRequest: Promise<unknown> | null = null;
const submittedSessionIds = new Set<string>();

function canCalculateResults(): boolean {
  return clientSession.hasValidAnswers() && !clientSession.getResults();
}

export const sendResults = createAsyncThunk(
  'data/sendResults',
  async (_, { rejectWithValue }) => {
    try {
      const sessionData = clientSession.get();

      const savedResults = clientSession.getResults();

      if (savedResults) {
        return savedResults;
      }

      const sessionId = sessionData?.sessionId;

      if (!canCalculateResults() || !sessionId) {
        return rejectWithValue('no answers provided');
      }

      if (submittedSessionIds.has(sessionId)) {
        if (resultsRequest) {
          return resultsRequest;
        }

        return rejectWithValue('results already requested');
      }

      if (!resultsRequest) {
        submittedSessionIds.add(sessionId);
        resultsRequest = fetchWithRetry(`https://mako-political-match-api.rabbicdn.com/api/v1/results`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            versionId: sessionData!.version,
            partyAId: sessionData!.partyAId,
            partyBId: sessionData!.partyBId,
            answers: sessionData!.answers,
          }),
        })
          .then(result => {
            const currentSession = clientSession.get();

            if (
              !isQuizResults(result) ||
              !currentSession ||
              currentSession.sessionId !== sessionId
            ) {
              throw new Error('Invalid results');
            }

            clientSession.saveResults(result);
            return result;
          })
          .catch(error => {
            submittedSessionIds.delete(sessionId);
            throw error;
          })
          .finally(() => {
            resultsRequest = null;
          });
      }

      return resultsRequest;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : 'Unknown error');
    }
  },
  {
    condition: (_, { getState }) => {
      const { loadingResults } = (getState() as RootState).data;

      if (loadingResults || clientSession.getResults()) {
        return false;
      }

      return canCalculateResults();
    },
  }
);
